/** The providers the login gate offers. */
export const SocialLoginProviders = {
  APPLE: 'apple',
  GOOGLE: 'google',
} as const;

export const SOCIAL_LOGIN_PROVIDERS = [
  SocialLoginProviders.APPLE,
  SocialLoginProviders.GOOGLE,
] as const;

export type SocialLoginProvider = (typeof SOCIAL_LOGIN_PROVIDERS)[number];

/**
 * Where the provider buttons were shown when the user made a choice. The
 * landing pages have one surface, the login gate. The desktop overlay and the
 * mobile menu both render it. The funnel adds `checkout` on its own side.
 */
export type SocialLoginPlacement = 'login_gate';

/**
 * What `AppleID.auth.signIn()` resolves to in popup mode. Apple sends `user` on
 * the first authorization only.
 */
export type AppleSignInResponse = {
  authorization: {
    id_token: string;
    code?: string;
    state?: string;
  };
  user?: {
    name?: {
      firstName?: string;
      lastName?: string;
    };
    email?: string;
  };
};

/**
 * The answer to `POST /auth/v1/exchange`, successful or not. Every field is
 * optional, because the backend can change or truncate the payload. A session
 * built from a missing token is worse than a failed login.
 *
 * The content of `detail` changes with `type`. For an unverified email it holds
 * an address. This matters most on the Google path, which keeps all provider
 * tokens on the backend. For `invalid-credentials` it holds the reason for the
 * refusal, which is for the developer and not for the user.
 *
 * @see https://consumer-api.floyt.com/auth/openapi
 */
export type ExchangeResponse = {
  access_token?: string;
  refresh_token?: string;
  id_token?: string;
  type?: string;
  detail?: string[];
};

/**
 * What a provider gives to `POST /auth/v1/exchange`.
 *
 * One field holds both credentials, because the endpoint takes one. `provider`
 * tells the endpoint how to read it. The Apple popup resolves with an ID token,
 * which the exchange verifies against the Apple keys. The Google popup returns
 * an authorization code, which the exchange redeems at Google.
 */
export type ExchangeCredential = { token: string };

/**
 * What `requestCode()` sends to the `callback`.
 *
 * There are two results: an authorization code, or the reason the Google window
 * returned none. Google guarantees neither field, so both are optional.
 *
 * @see https://developers.google.com/identity/oauth2/web/reference/js-reference
 */
export type GoogleCodeResponse = {
  code?: string;
  scope?: string;
  state?: string;
  error?: string;
  error_description?: string;
};

/**
 * What `error_callback` receives. It reports the failures that happen before a
 * window can answer, most of all a blocked popup.
 */
export type GoogleIdentityError = {
  type: string;
  message?: string;
};

/**
 * The configuration of the code client.
 *
 * This flow has no `nonce` field. The code belongs to the client id, and Google
 * redeems it one time only.
 *
 * Do not set `redirect_uri`. It changes Google to a full-page redirect.
 * `ux_mode: 'popup'` sends the code back to this document.
 */
export type GoogleCodeClientConfig = {
  client_id: string;
  scope: string;
  ux_mode: 'popup' | 'redirect';
  callback: (response: GoogleCodeResponse) => void;
  error_callback?: (error: GoogleIdentityError) => void;
};

export type GoogleCodeClient = {
  requestCode: () => void;
};

export type GoogleAccountsOauth2 = {
  initCodeClient: (config: GoogleCodeClientConfig) => GoogleCodeClient;
};

export type AppleIdAuth = {
  init: (config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    nonce?: string;
    state?: string;
    usePopup: boolean;
  }) => void;
  signIn: () => Promise<AppleSignInResponse>;
};

/**
 * The shape the identity SDK puts on `window.google`.
 *
 * Google Maps declares the same global with a type that requires `maps`. A
 * second declaration intersects with that type and makes both halves required.
 * Read the identity namespace through this shape instead.
 *
 * Every level is optional. A page that loaded Maps alone has the global with
 * no `accounts` on it.
 */
export type WindowWithGoogleIdentity = {
  google?: {
    accounts?: {
      oauth2?: GoogleAccountsOauth2;
    };
  };
};

declare global {
  interface Window {
    AppleID?: {
      auth: AppleIdAuth;
    };
  }
}
